import React, { useMemo, useCallback } from 'react'
import { useDashboard } from '@aytm/app/insights/dashboard/components/hooks'
import { BLOCK_TYPES } from '@aytm/app/insights/dashboard/lib/config'
import { BlockModel, BlocksOrderedMap } from '@aytm/app/insights/dashboard/models/BlockModel'
import staticStore from '@aytm/app/insights/dashboard/stores/static.store'
import { areObjectsEqual } from '@aytm/app/insights/dashboard/lib/comparison'
import type { ReportsArray, ReportType, ReportsContextType } from './types'
import { poolDispatch } from '@aytm/app/insights/dashboard/duckness/poolDispatch'
import {
  makeActivateViewPortalAction,
  makeRevertCurrentViewPortalAction,
  makeReplaceViewPortalAction,
  makeSaveCurrentViewPortalAction,
  makeAddViewPortalAction,
  makeDeleteViewPortalAction
} from '@aytm/app/insights/dashboard/duckness/ducks/PortalDuck/PortalDuck.actionCreators'
import { useIsLoading } from '@aytm/app/insights/dashboard/components/hooks/useIsLoading'
import { ItemType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import { itemsOrderChanged } from '@aytm/app/insights/dashboard/helpers/items'

const EXCLUDE_FIELDS_IN_COMPARISON = [
  'purpose',
  'timelineOffset',
  'timelineZoom',
  'hideBlankColumns',
  'updatedAt',
  'letterStore',
  'version'
]

export const TITLE_MAX_LENGTH = 120

export const ReportsContext = React.createContext<ReportsContextType>(null)

const handleSave = (title?: string | null) => {
  poolDispatch(makeSaveCurrentViewPortalAction({ title: title?.trim() }))
}
const handleDeleteBlock = (report: ReportType) => {
  if (!report) {
    return
  }
  poolDispatch(makeDeleteViewPortalAction(report.id))
}

export function useReportsImplementation() {
  const { currentSchema, remoteSchema, blockModel, blockUID } = useDashboard()

  const currentBlocks = useMemo<BlocksOrderedMap>(() => currentSchema?.get?.('blocks'), [currentSchema])
  const remoteBlocks = useMemo<BlocksOrderedMap>(() => remoteSchema?.get?.('blocks'), [remoteSchema])
  const isLoading = useIsLoading()
  const reports = useMemo<ReportsArray>(() => {
    if (!blockModel?.uid) {
      return []
    }
    const blockMapper = getReportForBlockWithSelected(blockModel.uid, remoteBlocks)
    return [currentBlocks.find(block => 'portal' === block.purpose)]
      .map(blockMapper)
      .concat(
        currentBlocks
          .toArray()
          .filter(block => block.type === blockModel?.type && 'view' === block.purpose)
          .map(blockMapper)
      )
      .filter(Boolean)
  }, [blockModel?.type, blockModel?.uid, currentBlocks, remoteBlocks])

  const isCurrentModified = useMemo<boolean>(
    () => (isLoading ? false : reports.find(({ isActive }) => isActive)?.isModified ?? false),
    [isLoading, reports]
  )

  const makeBlockActive = useCallback(
    (reportId?: null | string) => {
      if (!reportId || reportId === blockModel?.uid) {
        return
      }
      const report = reports.find(report => report.id === reportId)
      if (!report) {
        return
      }
      poolDispatch(makeActivateViewPortalAction(report.id))
    },
    [blockModel?.uid, reports]
  )

  const handleRevertCurrent = useCallback(() => {
    poolDispatch(makeRevertCurrentViewPortalAction())
  }, [])

  const isTitleInvalidCheck = useMemo(() => isTitleTakenOrInvalid(reports, { uid: blockUID }), [blockUID, reports])

  const swapBlocks = useCallback(
    ({ from, to, append }: { from: Pick<ReportType, 'id'>; to: Pick<ReportType, 'id'>; append?: boolean }) => {
      poolDispatch(
        makeReplaceViewPortalAction(
          from.id,
          to.id,
          reports.map(({ id }) => id),
          append
        )
      )
    },
    [reports]
  )

  const blockTitle = useMemo<string>(() => (blockModel ? blockModel.get('title') || '' : ''), [blockModel])

  const handleSaveAs = useCallback(
    (title?: string | null) => {
      const newTitle = !title || title.trim?.() === blockTitle ? getNewTitle(blockModel, reports) : title
      poolDispatch(makeAddViewPortalAction(newTitle))
    },
    [blockModel, blockTitle, reports]
  )

  return useMemo(
    () =>
      ({
        isCurrentModified,
        blockModel,
        reports,
        isTitleInvalidCheck,
        onRevertCurrent: handleRevertCurrent,
        onMakeActive: makeBlockActive,
        onReplace: swapBlocks,
        onDelete: handleDeleteBlock,
        onSave: handleSave,
        onSaveAs: handleSaveAs
      }) as ReportsContextType,
    [
      blockModel,
      handleRevertCurrent,
      handleSaveAs,
      isCurrentModified,
      isTitleInvalidCheck,
      makeBlockActive,
      reports,
      swapBlocks
    ]
  )
}

const newTitleSuffixGen = function* (max = 0) {
  yield 'copy'
  let i = 2
  while (true) {
    yield `copy ${i++}`
    if (max && i >= max) {
      break
    }
  }
}

export function getBlockTitle(block: BlockModel): string {
  const { purpose, type, title } = block
  if (title) {
    return title
  }
  if ('portal' === purpose) {
    return BLOCK_TYPES.CONCEPT_LAB === type ? 'Untitled Concept' : 'Untitled Tracker'
  }
  return 'Untitled Report'
}

const reservedTitleNames = new Set(['untitled concept', 'untitled tracker', 'untitled view'])

function isTitleTakenOrInvalid(reports: ReportsArray, { uid, isNew }: { uid?: string; isNew?: boolean }) {
  const takenTitles = new Set(
    (isNew ? reports : reports.filter(report => uid !== report.id)).map(report => report.title)
  )

  return (title?: string | null) =>
    !title || !title.trim()
      ? { invalid: true, validation: 'required' }
      : title.trim().length > TITLE_MAX_LENGTH
      ? { invalid: true, validation: 'max_len' }
      : takenTitles.has(title.trim()) || reservedTitleNames.has(title.trim().toLowerCase())
      ? { invalid: true, validation: 'uniqueness' }
      : { invalid: false }
}

function getNewTitle(block: BlockModel, reports: ReportsArray) {
  const invalidTitleCheck = isTitleTakenOrInvalid(reports, { isNew: true })

  let blockTitle = getBlockTitle(block)

  for (const suffix of newTitleSuffixGen(100)) {
    if (blockTitle.endsWith(suffix)) {
      blockTitle = blockTitle.slice(0, -suffix.length).trim()
      break
    }
  }
  while (blockTitle) {
    for (const suffix of newTitleSuffixGen(9999)) {
      const tryingTitle = `${blockTitle} ${suffix}`
      if (invalidTitleCheck(tryingTitle).invalid) {
        continue
      }
      return tryingTitle
    }
    blockTitle = blockTitle.slice(0, -1).trim()
  }

  return 'Untitled'
}

function getReportForBlockWithSelected(selectedUID: string | null, remoteBlocks: BlocksOrderedMap) {
  return (currentBlock: BlockModel): ReportType => {
    if (!currentBlock || !remoteBlocks) {
      return null
    }
    const remoteBlock: BlockModel = remoteBlocks.get(currentBlock.get('uid'))
    const { uid, purpose, type } = currentBlock
    return {
      id: uid,
      type,
      isDefault: 'portal' === purpose,
      title: getBlockTitle(currentBlock),
      isActive: selectedUID === uid,
      isModified: staticStore.isSharedView
        ? false
        : !areObjectsEqual(remoteBlock?.toJS(), currentBlock?.toJS(), {
            exclude: EXCLUDE_FIELDS_IN_COMPARISON,
            exceptions: {
              items: (a: unknown, b: unknown) => !itemsOrderChanged(a as ItemType[], b as ItemType[]),
              sections: (a: unknown, b: unknown) => (a && b ? areObjectsEqual(a, b) : true),
              milestones: treatEmptyArraysAndNullsEqually
            }
          })
    }
  }
}

function treatEmptyArraysAndNullsEqually(a: unknown, b: unknown) {
  const lengthA = (a as { length?: number })?.length ?? 0
  const lengthB = (b as { length?: number })?.length ?? 0

  if (lengthA === 0 && lengthB === 0) {
    return true
  }
  return a && b ? areObjectsEqual(a, b) : false
}
